import { Dictionary } from 'lodash';
import { Layout } from 'react-grid-layout';

import { WidgetSettings } from '../../userSettings';

import {
  WidgetDefaultSize,
  widgetsDefaultSize,
  widgetsSizeLimits,
} from './workspaceLayoutSettings';

export const buildWorkspaceLayout = (
  cols: number,
  widgets: WidgetSettings[],
  defaultSizes?: Dictionary<WidgetDefaultSize>,
) => {
  const defaults = {
    sizes: defaultSizes ? defaultSizes : widgetsDefaultSize,
  };

  const matrix: string[][] = [[]];
  const pointer = { x: 0, y: 0 };

  const movePointer = () => {
    pointer.x = (pointer.x + 1) % cols;
    pointer.y = pointer.y + (pointer.x ? 0 : 1);
  };

  const cellDoesNotFit = (w: number, h: number): boolean => {
    if (pointer.x + w > cols) {
      return true;
    }

    for (let y = pointer.y; y < pointer.y + h; y++) {
      for (let x = pointer.x; x < pointer.x + w; x++) {
        if (matrix[y]?.[x]) {
          return true;
        }
      }
    }

    return false;
  };

  const findSuitablePosition = (w: number, h: number) => {
    while (cellDoesNotFit(w, h)) {
      movePointer();
    }
  };

  const fillMatrix = (layout: Layout) => {
    for (let y = layout.y; y < layout.y + layout.h; y++) {
      if (!matrix[y]) {
        matrix[y] = [];
      }

      for (let x = layout.x; x < layout.x + layout.w; x++) {
        matrix[y][x] = 'x';
      }
    }
  };

  const buildCellLayout = ({ id, widget, size }: WidgetSettings) => {
    const defaultW = defaults.sizes[widget]?.w || 1;
    const defaultH = defaults.sizes[widget]?.h || 1;

    const maxW = Math.min(
      widgetsSizeLimits[widget]?.maxW || 2 || defaultW,
      cols,
    );
    const minW = Math.max(widgetsSizeLimits[widget]?.minW || 1 || defaultW);

    const maxH = widgetsSizeLimits[widget]?.maxH || 2 || defaultH;

    const w = Math.max(Math.min(size?.w || defaultW, cols, maxW), minW);
    const h = Math.min(size?.h || defaultH, maxH);

    findSuitablePosition(w, h);

    const layout: Layout = { i: id, ...pointer, w, h, maxW, maxH, minW };

    fillMatrix(layout);

    return layout;
  };

  return widgets.reduce((accumulator, widgetSettings) => {
    return [...accumulator, buildCellLayout(widgetSettings)];
  }, [] as Layout[]);
};
